import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, Home, Share2, ChevronRight, Headphones, GraduationCap, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { getSovereigntyLevel } from '../data/quizData';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Header from './Header';

const QuizResults = ({ score, onRetake }) => {
  const { isDark } = useTheme();
  const { user, getQuizResults } = useAuth();
  const navigate = useNavigate();
  const level = getSovereigntyLevel(score);
  const quizHistory = getQuizResults();

  const shareText = `I scored ${score}/80 on the Unshackled Living Sovereignty Quiz - I'm "${level.level}"! 🔓 Take the quiz: `;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Unshackled Living Sovereignty Quiz',
        text: shareText,
        url: window.location.origin + '/quiz'
      });
    } else {
      navigator.clipboard.writeText(shareText + window.location.origin + '/quiz');
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Score Card */}
          <div className={`rounded-3xl overflow-hidden border ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-xl'}`}>
            {/* Header with gradient */}
            <div className={`bg-gradient-to-r ${level.color} p-8 text-center`}>
              <span className="text-6xl mb-4 block">{level.emoji}</span>
              <h1 className="text-3xl font-bold text-white mb-2">
                {level.level}
              </h1>
              <p className="text-white/90 text-lg">
                Your Sovereignty Score
              </p>
            </div>

            {/* Score display */}
            <div className="p-8 text-center border-b border-stone-800">
              <div className="inline-flex items-baseline gap-2">
                <span className={`text-7xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  {score}
                </span>
                <span className={`text-2xl ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                  / 80
                </span>
              </div>
              
              {/* Score bar */}
              <div className="mt-6 max-w-md mx-auto">
                <div className={`h-4 rounded-full ${isDark ? 'bg-stone-800' : 'bg-stone-200'}`}>
                  <div 
                    className={`h-full rounded-full bg-gradient-to-r ${level.color} transition-all duration-1000`}
                    style={{ width: `${(score / 80) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs">
                  <span className={isDark ? 'text-stone-600' : 'text-stone-400'}>System Dependent</span>
                  <span className={isDark ? 'text-stone-600' : 'text-stone-400'}>Unshackled</span>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="p-8">
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                {level.summary}
              </h2>
              <p className={`leading-relaxed whitespace-pre-line ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                {level.description}
              </p>

              {/* Good news */}
              <div className={`mt-6 p-5 rounded-2xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  The Good News
                </h3>
                <p className={isDark ? 'text-stone-300' : 'text-stone-700'}>
                  {level.goodNews}
                </p>
              </div>

              {/* Leverage point */}
              <div className="mt-6">
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                  👉 Your Biggest Leverage Point
                </h3>
                <p className={isDark ? 'text-stone-400' : 'text-stone-600'}>
                  {level.leveragePoint}
                </p>
              </div>

              {/* Next steps */}
              <div className="mt-8">
                <h3 className={`font-semibold mb-4 ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                  Your Next Steps
                </h3>
                <div className="space-y-3">
                  {level.nextSteps.map((step, index) => (
                    <div 
                      key={index}
                      className={`flex items-start gap-3 p-4 rounded-xl ${isDark ? 'bg-stone-800/50' : 'bg-stone-100'}`}
                    >
                      <ChevronRight className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                      <span className={isDark ? 'text-stone-300' : 'text-stone-700'}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Resources */}
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <a
                  href="https://www.youtube.com/@UnshackledLiving"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-4 p-5 rounded-2xl border transition-all ${
                    isDark 
                      ? 'bg-stone-800/50 border-stone-700 hover:border-amber-500/30' 
                      : 'bg-stone-50 border-stone-200 hover:border-amber-500/50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                    <Headphones className={`w-6 h-6 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>Listen to the Podcast</h4>
                    <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Unshackled Living</p>
                  </div>
                </a>

                <a
                  href="https://maven.com/the-web3-prof/crypto-fundamentals"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-4 p-5 rounded-2xl border transition-all ${
                    isDark 
                      ? 'bg-stone-800/50 border-stone-700 hover:border-amber-500/30' 
                      : 'bg-stone-50 border-stone-200 hover:border-amber-500/50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                    <GraduationCap className={`w-6 h-6 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>Take the Course</h4>
                    <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Bitcoin Fundamentals</p>
                  </div>
                </a>
              </div>

              {/* 7-Day Course CTA */}
              <div className={`mt-6 p-6 rounded-2xl border-2 border-dashed ${isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-400 bg-amber-50'}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                    <BookOpen className={`w-7 h-7 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      Ready to Take Action?
                    </h3>
                    <p className={`text-sm mb-4 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                      Start your 7-day sovereignty journey with daily lessons and guided journaling. <strong>Only $10</strong> for lifetime access.
                    </p>
                    <Button
                      onClick={() => navigate('/course/checkout')}
                      className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold"
                    >
                      Get Course Access - $10
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Button
              onClick={onRetake}
              variant="outline"
              className={isDark ? 'border-stone-700 text-stone-300 hover:bg-stone-800' : 'border-stone-300 text-stone-600 hover:bg-stone-100'}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retake Quiz
            </Button>
            
            <Button
              onClick={handleShare}
              className="bg-amber-500 hover:bg-amber-600 text-stone-950"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Results
            </Button>
            
            <Link to="/">
              <Button
                variant="outline"
                className={isDark ? 'border-stone-700 text-stone-300 hover:bg-stone-800' : 'border-stone-300 text-stone-600 hover:bg-stone-100'}
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Quiz History */}
          {quizHistory.length > 1 && (
            <div className={`mt-12 p-6 rounded-2xl border ${isDark ? 'bg-stone-900/30 border-stone-800' : 'bg-white border-stone-200'}`}>
              <h3 className={`font-semibold mb-4 ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                Your Quiz History
              </h3>
              <div className="space-y-2">
                {quizHistory.slice().reverse().map((result, index) => (
                  <div 
                    key={result.id}
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      index === 0 
                        ? isDark ? 'bg-amber-500/10' : 'bg-amber-50'
                        : isDark ? 'bg-stone-800/30' : 'bg-stone-50'
                    }`}
                  >
                    <div>
                      <span className={`font-medium ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                        {result.level}
                      </span>
                      <span className={`text-sm ml-2 ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        {new Date(result.date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`font-bold ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>
                      {result.score}/80
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizResults;
